import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star, Users, TrendingUp, Award, Zap, Shield, Sparkles, Printer } from "lucide-react";
const Index = () => {
  const handlePrint = () => {
    window.print();
  };

  const handleHighFidelityPrint = () => {
    // Create a new window with high-fidelity print styles
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const currentContent = document.documentElement.outerHTML;
      
      // Enhanced print styles for exact screen replication
      const printStyles = `
        <style>
          @media print {
            @page {
              size: A4;
              margin: 0.2in;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            body {
              background: linear-gradient(135deg, #f1f5f9 0%, #ffffff 100%) !important;
              font-size: 10px !important;
              line-height: 1.3 !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            .no-print {
              display: none !important;
            }
            
            /* Preserve exact layout */
            .print-card {
              margin-bottom: 1.5rem !important;
              page-break-inside: avoid;
              overflow: visible !important;
            }
            
            /* Preserve all gradients and colors */
            .dnuvo-gradient {
              background: linear-gradient(135deg, hsl(95 20% 15%), hsl(28 12% 57%)) !important;
              color: white !important;
            }
            
            /* Preserve cover page styling */
            [style*="linear-gradient(135deg, #f8fafc"] {
              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%) !important;
            }
            
            /* Preserve text gradients */
            [style*="linear-gradient(45deg, hsl(95 20% 15%)"] {
              background: linear-gradient(45deg, hsl(95 20% 15%), hsl(14 78% 67%)) !important;
              -webkit-background-clip: text !important;
              -webkit-text-fill-color: transparent !important;
              background-clip: text !important;
            }
            
            /* Preserve all background colors */
            .bg-blue-50 { background-color: #eff6ff !important; }
            .bg-green-50 { background-color: #f0fdf4 !important; }
            .bg-orange-50 { background-color: #fff7ed !important; }
            .bg-purple-50 { background-color: #faf5ff !important; }
            .bg-gray-50 { background-color: #f9fafb !important; }
            .bg-white\/80 { background-color: rgba(255, 255, 255, 0.8) !important; }
            
            /* Preserve grid layouts */
            .grid { display: grid !important; }
            .md\:grid-cols-2 { grid-template-columns: repeat(2, 1fr) !important; }
            .md\:grid-cols-3 { grid-template-columns: repeat(3, 1fr) !important; }
            .lg\:grid-cols-4 { grid-template-columns: repeat(4, 1fr) !important; }
            
            /* Preserve shadows */
            .shadow-2xl { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important; }
            .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1) !important; }
            
            /* Preserve borders and rounded corners */
            .rounded-lg { border-radius: 0.5rem !important; }
            .border { border: 1px solid #e5e7eb !important; }
            .border-2 { border-width: 2px !important; }
            .border-orange-400 { border-color: #fb923c !important; }
            
            /* Preserve spacing */
            .space-y-8 > * + * { margin-top: 2rem !important; }
            .space-y-6 > * + * { margin-top: 1.5rem !important; }
            .space-y-4 > * + * { margin-top: 1rem !important; }
            .space-y-3 > * + * { margin-top: 0.75rem !important; }
            .space-y-2 > * + * { margin-top: 0.5rem !important; }
            
            /* Preserve text colors */
            .text-blue-900 { color: #1e3a8a !important; }
            .text-green-900 { color: #14532d !important; }
            .text-orange-900 { color: #9a3412 !important; }
            .text-purple-800 { color: #6b21a8 !important; }
            .text-gray-700 { color: #374151 !important; }
            .text-gray-600 { color: #4b5563 !important; }
            .text-white { color: white !important; }
            
            /* Preserve badges */
            .badge {
              display: inline-block !important;
              padding: 0.25rem 0.75rem !important;
              border-radius: 9999px !important;
              font-size: 0.75rem !important;
              font-weight: 500 !important;
              line-height: 1 !important;
            }
            
            /* Ensure images maintain quality */
            img {
              max-width: 100% !important;
              height: auto !important;
              page-break-inside: avoid;
            }
          }
        </style>
      `;
      
      printWindow.document.write(currentContent.replace('</head>', printStyles + '</head>'));
      printWindow.document.close();
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 1000);
    }
  };

  // Main content (public access)
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b no-print">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-12 bg-white rounded-lg flex items-center justify-center p-1">
              <img src="./images/dnuvo.png" alt="d.nuvo Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Brand Kit </h1>
              <p className="text-sm text-gray-600">Science-Driven Skincare • Complete Product Range</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button onClick={handlePrint} className="flex items-center space-x-2 bg-green-600 hover:bg-green-700">
              <Printer className="w-4 h-4" />
              <span>Standard Print</span>
            </Button>
            <Button onClick={handleHighFidelityPrint} className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4" />
              <span>High-Fidelity Print</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8 print:space-y-0">
        
        {/* Cover Page */}
        <div className="print-card">
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 p-12 min-h-screen flex flex-col justify-center items-center text-center" style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)'
          }}>
            <div className="mb-8">
              
            </div>
            <div className="space-y-6">
              <h1 className="text-6xl font-bold mb-4" style={{
                background: 'linear-gradient(45deg, hsl(95 20% 15%), hsl(14 78% 67%))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                d.nuvo®
              </h1>
              <h2 className="text-3xl font-semibold mb-6" style={{
                color: 'hsl(95 20% 15%)'
              }}>
                Brand Kit & Media Release
              </h2>
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 max-w-2xl border shadow-lg" style={{
                borderColor: 'hsl(14 78% 67%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)'
              }}>
                <p className="text-xl mb-4" style={{
                  color: 'hsl(95 20% 25%)'
                }}>
                  Science-Driven Derma Skincare
                </p>
                <p className="text-lg" style={{
                  color: 'hsl(14 78% 45%)'
                }}>
                  Revolutionary Ceramided™ Technology • Stabilized Pure Vitamin C 15.5%
                </p>
              </div>
              <div className="mt-8" style={{
                color: 'hsl(95 20% 35%)'
              }}>
                <p className="text-sm">For Editorial Beauty Writers & Influencers</p>
                <p className="text-xs mt-2">Etheria Skincare Pte Ltd • Southeast Asia</p>
              </div>
            </div>
          </div>
        </Card>
        </div>
        
        {/* Intro Page - Introduction Letter */}
        <div className="print-card">
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-8 print-header">
            <CardHeader className="p-0">
              <CardTitle className="text-4xl font-bold mb-4 text-center">d.nuvo® Brand Kit</CardTitle>
              <p className="text-xl text-center text-slate-200">Introduction & Media Release</p>
            </CardHeader>
          </div>
          <CardContent className="p-8">
            <div className="prose max-w-none">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-slate-800 mb-2">Subject: For Immediate Release</h3>
                <h2 className="text-3xl font-bold text-dnuvo-primary mb-4">d.nuvo® Unveils a New Era of Skin Science in Southeast Asia</h2>
              </div>
              
              <div className="mb-6">
                <p className="text-lg font-semibold text-slate-700 mb-2">To: Editorial Beauty Writers & Influencers,</p>
                <div className="bg-blue-50 p-4 rounded-lg mt-4">
                  <p className="text-sm text-blue-800"><strong>Primary Audience:</strong> Editorial Beauty Writers - Focus on science, efficacy, and clinical research</p>
                  <p className="text-sm text-blue-800"><strong>Secondary Audience:</strong> Influencers - Focus on proven results, benefits, and user experience</p>
                </div>
              </div>
              
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>We are thrilled to introduce <strong>d.nuvo®</strong>, a revolutionary, science-driven derma skincare brand making its debut in Southeast Asia. d.nuvo unveils a five-product range infused with patented <strong>Ceramided™ absorption technology</strong> and stabilized <strong>Pure Vitamin C (LAA 15.5%)</strong>, together with an innovative Sun Care line offering dual protection against UV rays and free radicals — introducing a new era of skincare performance.</p>
                
                <p>This launch marks a significant milestone in skincare, moving beyond surface-level treatments to address the fundamental challenge of absorption. At the core of d.nuvo® is the <strong>patented Ceramided™ technology</strong>, a breakthrough delivery system clinically proven to carry active ingredients <strong>15x deeper into the skin</strong>. This, combined with a masterfully stabilized 15.5% Pure Vitamin C formula, delivers visible, transformative results without irritation.</p>
                
                <p>This brand kit contains everything you need to discover the science, story, and products behind d.nuvo®. We invite you to explore how we are making luxury-level efficacy accessible and setting a new standard for deep efficacy in skincare.</p>
                
                <p>We believe d.nuvo® will resonate deeply with your audience, who seek tangible, science-backed results. We look forward to the possibility of collaborating with you.</p>
              </div>
              
              <div className="mt-8 p-6 bg-slate-50 rounded-lg">
                <p className="text-lg font-semibold text-slate-800 mb-2">Warm regards,</p>
                <p className="text-xl font-bold text-dnuvo-primary">The d.nuvo® Team</p>
                <p className="text-gray-600">Etheria Skincare Pte Ltd</p>
              </div>
              
              <div className="mt-6 text-center">
                <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4" />
                  <span>Confidential Brand Information - For Media & Influencer Use Only</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Brand Overview */}
        <div className="print-card">
        <Card className="overflow-hidden">
          <div className="dnuvo-gradient text-white p-6 print-header">
            <CardHeader className="p-0">
              <CardTitle className="text-3xl font-bold mb-2">d.nuvo® Brand Overview</CardTitle>
              <p className="text-white/80 text-lg print:text-black">Revolutionary Science-Driven Derma Skincare</p>
            </CardHeader>
          </div>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">Brand Positioning</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start space-x-2">
                    <Star className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Premium science-driven skincare brand</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Award className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <span>Patented Ceramided™ technology</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Shield className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>37+ years of R&D expertise</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <TrendingUp className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span>Luxury efficacy at accessible prices</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-gray-900">Key Markets</h4>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Singapore</Badge>
                  <Badge variant="secondary">Malaysia</Badge>
                  <Badge variant="secondary">Indonesia</Badge>
                  <Badge variant="secondary">Philippines</Badge>
                </div>
                <div className="mt-4">
                  <div className="w-full h-64 rounded-lg overflow-hidden">
                    <img src="https://static-us-img.skywork.ai/prod/user/head_picture/1984532644934107136_C1 2. SOCIAL POST - Brand_01 05022025 1200x600_edited.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="d.nuvo Complete Product Range" className="w-full h-full object-contain bg-white" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Brand Introductions */}
            <div className="mt-8 space-y-6">
              
              {/* About d.nuvo */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-blue-900">About d.nuvo®</h3>
                <p className="text-gray-700 leading-relaxed text-sm">
                  d.nuvo® is a <strong>science-driven derma skincare brand</strong> founded on the principle of <strong>deep efficacy</strong>. 
                  Built on a <strong>37-year legacy of cosmetic research</strong>, the brand's core innovations—patented 
                  <strong>Ceramided™ delivery technology</strong> and <strong>stabilized Pure Vitamin C</strong>—solve the fundamental 
                  challenge of absorption. d.nuvo® delivers <strong>clinically validated, non-irritating formulas</strong> that offer 
                  a luxury experience at accessible <strong>masstige prices</strong>, bridging the gap between clinical credibility 
                  and daily usability.
                </p>
              </div>
              
              {/* About Etheria Skincare */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold mb-3 text-green-900">About Etheria Skincare Pte Ltd</h3>
                <p className="text-gray-700 leading-relaxed text-sm">Etheria Skincare Pte Ltd is the official and exclusive brand partner for d.nuvo® in Singapore, Malaysia, Indonesia, and the Philippines. Committed to bringing cutting-edge, science-backed beauty to Southeast Asia, Etheria Skincare curates brands that deliver tangible results and cater to the unique needs of the region's consumers.</p>
              </div>
              
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Card 2: Brand Story & Philosophy */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Award className="w-6 h-6" style={{
                color: 'hsl(14 78% 67%)'
              }} />
              <span>Brand Story & Philosophy</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-3" style={{
                  color: 'hsl(95 20% 15%)'
                }}>Science-Driven Innovation</h3>
                <p className="text-gray-700 mb-4">d.nuvo® is born from the synthesis of Dermo-cosmetics and Nuovo (New), signifying a new era in skin science. Founded on decades of research to solve skincare's most fundamental challenge: absorption.</p>
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 italic">"We believe that nearly 90% of ingredients that spark momentary consumer interest offer little to no real benefit. It's estimated that only a small percentage of skincare products on the market are actually absorbed beyond the stratum corneum. We created d.nuvo to change that."- Founder, d.nuvo Korea</p>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3" style={{
                  color: 'hsl(95 20% 15%)'
                }}>Our Mission</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{
                      backgroundColor: 'hsl(14 78% 67%)'
                    }}></div>
                    <span>Solve the fundamental challenge of ingredient absorption in skincare</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{
                      backgroundColor: 'hsl(14 78% 67%)'
                    }}></div>
                    <span>Deliver luxury-level efficacy at accessible masstige prices</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{
                      backgroundColor: 'hsl(14 78% 67%)'
                    }}></div>
                    <span>Bridge the gap between clinical credibility and daily usability</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{
                      backgroundColor: 'hsl(14 78% 67%)'
                    }}></div>
                    <span>Provide science-backed results without irritation</span>
                  </li>
                </ul>
                <div className="mt-4">
                  
                  <div className="flex flex-wrap gap-1">
                    
                    
                    
                    
                    
                    
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Technology & Unique Selling Points - Moved after Brand Story */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Zap className="w-6 h-6 text-yellow-600" />
              <span>Technology & Unique Selling Points</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">Two Revolutionary Pillars of d.nuvo® Science</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              
              {/* Pillar 1: Ceramided Technology */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold mb-4 text-blue-900">🔬 Pillar 1: Ceramided™ Technology - A Breakthrough in Delivery (Patent No. 40-2321146)</h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-blue-800">Superior Delivery System</h4>
                    <div className="bg-white p-4 rounded-lg border-l-4 border-blue-400 mb-4">
                      <p className="text-gray-700 text-sm leading-relaxed">
                        The <strong>ceramide capsule is recognized by the skin</strong>, allowing it to transform elastically and 
                        travel deep into the dermal layers <strong>without breaking</strong>, safely delivering its active payload.
                      </p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border-l-4 border-green-400">
                      <h5 className="font-bold text-green-800 mb-2">Barrier Reinforcement</h5>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        After releasing the active ingredient, the <strong>ceramide shell itself is deposited into the 
                        intercellular lipid matrix</strong>, actively repairing and strengthening the skin natural barrier from within. 
                        <strong>It doesn't just transport — it transforms.</strong>
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="w-full h-64 rounded-lg overflow-hidden mb-4">
                      <img src="./images/cer2.png" alt="Ceramided™ Technology Diagram" className="w-full h-full object-contain bg-white" />
                    </div>
                    <div className="bg-blue-100 p-4 rounded-lg">
                      <h5 className="font-bold text-blue-800 mb-2">🎯 Why Water-Based Matters for KOLs</h5>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• <strong>Quick absorption</strong> - No sticky residue for makeup application</li>
                        <li>• <strong>Lightweight feel</strong> - Perfect for humid SEA climate</li>
                        <li>• <strong>Superior penetration</strong> - Visible results for content creation</li>
                        <li>• <strong>Non-comedogenic</strong> - Safe for all skin types</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg">
                  <h5 className="font-bold text-blue-800 mb-3">🔬 Technical Specifications for Editorial Writers</h5>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Optimal pH (3.2):</strong> Permeability through the stratum corneum is highest below pH 3.5. 
                        d.nuvo's formula is precisely balanced for superior absorption and stability.
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700">
                        <strong>Water-Based Formula:</strong> As a hydrophilic ingredient, Pure Vitamin C is most effective in a 
                        water-based system. d.nuvo's formula ensures quick, non-sticky absorption and superior penetration 
                        compared to hybrid (oil + water) formulations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Pillar 2: Vitamin C */}
              <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold mb-4 text-orange-900">🏆 Pillar 2: Water-Based Pure Vitamin C 15.5% – The Gold Standard, Perfected</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border-l-4 border-orange-400">
                      <h5 className="font-bold text-orange-800 mb-2">🎯 Optimal Strength (15.5% Pure)</h5>
                      <p className="text-sm text-gray-700">
                        Based on stabilization science, the <strong>15% to 20% range is the sweet spot</strong> for maximum efficacy 
                        with minimal irritation.
                      </p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border-l-4 border-blue-400">
                      <h5 className="font-bold text-blue-800 mb-2">💧 Water-Based for Superior Absorption</h5>
                      <p className="text-sm text-gray-700">
                        d.nuvo stabilizes Vitamin C in a <strong>water-soluble state</strong>. Clinical imaging shows it 
                        <strong>penetrates deeper than hybrid formulas</strong>.
                      </p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border-l-4 border-green-400">
                      <h5 className="font-bold text-green-800 mb-2">🔒 Protected by Ceramided™</h5>
                      <p className="text-sm text-gray-700">
                        The patented capsule <strong>shields Vitamin C from oxidation</strong>, preserving its potency and clarity 
                        <strong>without artificial stabilizers or colorants</strong>.
                      </p>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border-l-4 border-purple-400">
                      <h5 className="font-bold text-purple-800 mb-2">✨ Powerful Benefits</h5>
                      <p className="text-sm text-gray-700">
                        Vitamin C offers <strong>powerful antioxidant protection against photoaging</strong>, supports 
                        <strong>collagen production</strong>, and promotes a <strong>clearer, more radiant complexion</strong>.
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="w-full h-64 rounded-lg overflow-hidden mb-4">
                      <img src="./images/appl2.jpg" alt="Apple Slice Treatment Results - d.nuvo Vitamin C Stability Test" className="w-full h-full object-contain bg-white" />
                    </div>
                    <div className="bg-orange-100 p-4 rounded-lg">
                      <h5 className="font-bold text-orange-800 mb-2">🍎 Apple Slice Treatment Results</h5>
                      <p className="text-sm text-orange-700">
                        Visual demonstration of d.nuvo® Vitamin C stability and antioxidant power. 
                        <strong>No browning, no oxidation</strong> - proof of superior formulation stability.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Card 3: Complete Product Range - Hero Products */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Sparkles className="w-6 h-6" style={{
                color: 'hsl(14 78% 67%)'
              }} />
              <span>Hero Products</span>
            </CardTitle>
            <p className="text-sm mt-2 text-[rgb(3,60,38)]">All products powered by patented Ceramided™ Technology (Patent No. 40-2321146)</p>
          </CardHeader>
          <CardContent>
            {/* Hero Products */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4" style={{
                color: 'hsl(95 20% 15%)'
              }}>Hero Products</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Fresh Ceramided™ Pure Vitamin C 15.5% Ampoule */}
                <div className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="w-full h-48 rounded-lg overflow-hidden mb-4">
                    <img src="https://static-us-img.skywork.ai/prod/user/head_picture/1984536156757737472_Blog 1 808x394_edited.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Fresh Ceramided™ Pure Vitamin C 15.5% Ampoule" className="w-full bg-white sk-edit-loading h-[191.9921875px] object-cover" />
                  </div>
                  <h4 className="font-semibold text-lg mb-2">Fresh Ceramided™ Pure Vitamin C 15.5% Ampoule</h4>
                  <p className="text-sm text-gray-600 mb-3">18ml / 0.61fl.oz</p>
                  <div className="space-y-2 mb-3">
                    <div className="flex flex-wrap gap-1">
                      <Badge style={{
                        backgroundColor: 'hsl(14 78% 67%)',
                        color: 'white'
                      }}>#PureVitaminC15.5%</Badge>
                      <Badge variant="outline">#AntiPigmentation</Badge>
                      <Badge variant="outline">#DeepAbsorption</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">Formulated with 15.5% pure Vitamin C in a water-based system for fast absorption, delivering brightening and antioxidant benefits for radiant skin.</p>
                  <div className="text-xs text-gray-600 space-y-2">
                    <div className="bg-green-50 p-3 rounded">
                      <p><strong>Enhanced Clinical Proof:</strong></p>
                      <ul className="list-disc list-inside space-y-1 mt-1">
                        <li><strong>15× deeper Vitamin C delivery</strong> - Human Skin Clinical Trial Center (2024)</li>
                        <li><strong>14× greater absorption rate</strong> vs conventional formulas</li>
                        <li><strong>Visible brightness in 3 days</strong> - Semyung University (2023)</li>
                        <li><strong>Freckle & pigmentation reduction in 2-4 weeks</strong></li>
                        <li><strong>Non-irritating formula</strong> - Advanced Science Lab (2025)</li>
                        <li><strong>Multi-benefit action:</strong> Acne, sebum control, wrinkle reduction</li>
                      </ul>
                    </div>
                    <p><strong>Key Ingredients:</strong> L-Ascorbic Acid (15.5%), Ectoine (7,000 ppm), Ferulic Acid, Bakuchiol, Ceramide NP</p>
                    <p><strong>Research Partners:</strong> Human Skin Clinical Trial Center, Semyung University, Advanced Science Lab</p>
                  </div>
                </div>

                {/* Fresh Ceramided™ Calming Ampoule */}
                <div className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="w-full h-48 rounded-lg overflow-hidden mb-4">
                    <img src="https://static-us-img.skywork.ai/prod/user/head_picture/1984536046290149376_1. BLOG - Trouble Calming 07032025_edited.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Fresh Ceramided™ Trouble Calming Ampoule" className="w-full bg-white sk-edit-loading h-[191.9921875px] object-cover" />
                  </div>
                  <h4 className="font-semibold text-lg mb-2">Fresh Ceramided™ Trouble Calming Ampoule</h4>
                  <p className="text-sm text-gray-600 mb-3">30ml / 1.01fl.oz</p>
                  <div className="space-y-2 mb-3">
                    <div className="flex flex-wrap gap-1">
                      <Badge style={{
                        backgroundColor: 'hsl(95 20% 15%)',
                        color: 'white'
                      }}>#TroubleSoothing</Badge>
                      <Badge variant="outline">#SebumControl</Badge>
                      <Badge variant="outline">#Bakuchiol</Badge>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">Intensive soothing care instantly absorbed to calm irritated, sensitive, and acne-prone skin. Uses patented ACZERO® complex of 7 anti-inflammatory herbs.</p>
                  <div className="text-xs text-gray-600 space-y-2">
                    <div className="bg-blue-50 p-3 rounded">
                      <p><strong>Enhanced Clinical Proof:</strong></p>
                      <ul className="list-disc list-inside space-y-1 mt-1">
                        <li><strong>Reduces acne & redness</strong> - visible improvement in 2-4 weeks</li>
                        <li><strong>Controls sebum production</strong> - clinically proven oil control</li>
                        <li><strong>Non-comedogenic formula</strong> - won't clog pores</li>
                        <li><strong>Gentle on sensitive skin</strong> - dermatologist tested</li>
                        <li><strong>Anti-inflammatory action</strong> - 7-herb ACZERO® complex</li>
                      </ul>
                    </div>
                    <p><strong>Key Ingredients:</strong> Patented ACZERO® (7-herb complex), Bakuchiol (plant retinol), Madecassoside, 5-Type Ceramide Complex</p>
                    <p><strong>Research Partners:</strong> Dermatology Research Institute, Clinical Safety Labs</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Card 4: Supporting Product Line */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle>Supporting Product Line</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4" style={{
                color: 'hsl(95 20% 15%)'
              }}>Supporting Product Line</h3>
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-4 rounded">
                <h4 className="font-bold text-orange-800 mb-2">🌟 NEW SUN CARE LINE</h4>
                <p className="text-sm text-orange-700">Introducing our breakthrough sun protection technology</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Aqua Dual C Sun Serum - NEW SUN CARE LINE */}
                <div className="border-2 border-orange-400 rounded-lg p-4 hover:shadow-lg transition-shadow bg-orange-50">
                  <div className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 inline-block">
                    🌟 NEW SUN CARE LINE
                  </div>
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                    <img src="https://static-us-img.skywork.ai/prod/user/head_picture/1984526980503379968_2.png?image_process=quality,q_90/resize,w_1280/format,webp" alt="Aqua Dual C Sun Serum" className="w-full h-full object-contain bg-white" />
                  </div>
                  <h4 className="font-semibold text-base mb-2">Aqua Dual C Sun Serum</h4>
                  <p className="text-sm text-gray-600 mb-2">50ml</p>
                  <div className="space-y-1 mb-2">
                    <Badge style={{
                      backgroundColor: 'hsl(14 78% 67%)',
                      color: 'white'
                    }} className="text-xs">#DualProtection</Badge>
                    <Badge variant="outline" className="text-xs">#SPF50_PA++++</Badge>
                  </div>
                  <p className="text-xs text-gray-700 mb-2">Next-generation sunscreen with dual defense: UV protection + Vitamin C antioxidant power. Lightweight, no white cast.</p>
                  <p className="text-xs text-gray-600"><strong>Key:</strong> Broad-Spectrum UV Filters, Stabilized Vitamin C, 8-Type Hyaluronic Acid</p>
                </div>

                {/* Fresh Ceramided™ Toner */}
                <div className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                    <img src="./images/Toner1_edited.jpg" alt="Fresh Ceramided™ Essential Toner" className="w-full h-full object-contain bg-white" />
                  </div>
                  <h4 className="font-semibold text-base mb-2">Fresh Ceramided™ Essential Toner</h4>
                  <p className="text-sm text-gray-600 mb-2">200ml / 6.7fl.oz</p>
                  <div className="space-y-1 mb-2">
                    <Badge style={{
                      backgroundColor: 'hsl(28 12% 57%)',
                      color: 'white'
                    }} className="text-xs">#8TypesHA</Badge>
                    <Badge variant="outline" className="text-xs">#5Ceramides</Badge>
                  </div>
                  <p className="text-xs text-gray-700 mb-2">Essence-level toner providing instant hydration and barrier reset. More than a simple toner - delivers first-step essence efficacy.</p>
                  <p className="text-xs text-gray-600"><strong>Key:</strong> 8-Type Hyaluronic Acid, 5-Type Ceramides, Calcium PCA</p>
                </div>

                {/* Fresh Ceramided™ Water Cream */}
                <div className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                    <img src="./images/Watercream1_edited.jpg" alt="Fresh Ceramided™ Water Cream" className="w-full h-full object-contain bg-white" />
                  </div>
                  <h4 className="font-semibold text-base mb-2">Fresh Ceramided™ Water Cream</h4>
                  <p className="text-sm text-gray-600 mb-2">80ml / 2.7fl.oz</p>
                  <div className="space-y-1 mb-2">
                    <Badge style={{
                      backgroundColor: 'hsl(28 12% 57%)',
                      color: 'white'
                    }} className="text-xs">#MoistureBomb</Badge>
                    <Badge variant="outline" className="text-xs">#Raffinose</Badge>
                  </div>
                  <p className="text-xs text-gray-700 mb-2">Ceramide and 10 types of Hyaluronic Acid deeply replenish moisture, while Raffinose forms a hydration layer for long-lasting, dewy skin.</p>
                  <p className="text-xs text-gray-600"><strong>Key:</strong> Bakuchiol, Acetyl Hexapeptide-8, 8-Type HA, Cupuaçu Seed Butter</p>
                </div>

                {/* Fresh Ceramided™ Eye Cream */}
                <div className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                    <img src="./images/Eyecream1_edited.jpg" alt="Fresh Ceramided™ Eye Cream" className="w-full h-full object-contain bg-white" />
                  </div>
                  <h4 className="font-semibold text-base mb-2">Fresh Ceramided™ Eye Cream</h4>
                  <p className="text-sm text-gray-600 mb-2">35ml / 1.18fl.oz</p>
                  <div className="space-y-1 mb-2">
                    <Badge style={{
                      backgroundColor: 'hsl(95 20% 15%)',
                      color: 'white'
                    }} className="text-xs">#AntiAging</Badge>
                    <Badge variant="outline" className="text-xs">#Munapsys™</Badge>
                  </div>
                  <p className="text-xs text-gray-700 mb-2">Acetyl Hexapeptide reduces fine lines around the eyes, while Bakuchiol supports healthy skin. Specially designed for delicate areas around the eyes and lips.</p>
                  <p className="text-xs text-gray-600"><strong>Key:</strong> Bakuchiol, Acetyl Hexapeptide-1, 5,000ppm Ceramide NP</p>
                </div>
              </div>
            </div>

            {/* Product Benefits Summary */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-3" style={{
                color: 'hsl(95 20% 15%)'
              }}>Complete Skincare Routine Benefits</h4>
              
              {/* Day-Night Routine Illustration */}
              <div className="mb-6">
                <div className="w-full h-64 rounded-lg overflow-hidden bg-white border">
                  <img src="./images/12. Full ritual kit.jpg" alt="d.nuvo Complete Ritual Kit - Full Skincare Routine" className="w-full h-full object-contain" />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">Complete d.nuvo® ritual kit - Full skincare routine with all products</p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <h5 className="font-semibold mb-2">Morning Routine</h5>
                  <p className="text-gray-600">Toner → Vitamin C Ampoule → Water Cream → Dual C Sun Serum</p>
                </div>
                <div>
                  <h5 className="font-semibold mb-2">Evening Routine</h5>
                  <p className="text-gray-600">Toner → Calming Ampoule → Eye Cream → Water Cream</p>
                </div>
                <div>
                  <h5 className="font-semibold mb-2">Key Results</h5>
                  <p className="text-gray-600">Brightening, Anti-aging, Soothing, Barrier Repair, UV Protection</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Card 5: Why Partner with d.nuvo */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Zap className="w-6 h-6 text-yellow-600" />
              <span>Technology & Unique Selling Points</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-blue-600">Ceramided™ Technology </h3>
                <div className="bg-blue-50 p-5 rounded-lg mb-6">
                  <h4 className="font-semibold mb-3 text-base">Patented Dual-Action Technology (Patent No. 40-2321146)</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• <strong>15x deeper skin penetration</strong> - Clinically proven delivery</li>
                    <li>• <strong>Enhanced stability</strong> - 30% increased absorption rate</li>
                    <li>• <strong>Barrier repair</strong> - Strengthens skin from within</li>
                    <li>• <strong>Dual-action effect</strong> - Delivers + repairs simultaneously</li>
                  </ul>
                </div>
                <div className="w-full h-64 rounded-lg overflow-hidden">
                  <img src="https://static-us-img.skywork.ai/prod/user/head_picture/1984962977959583744_image 5.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Ceramided™ Technology Diagram" className="w-full bg-white sk-edit-loading h-[255.99609375px] object-cover" />
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-4 text-orange-600">Stabilized Vitamin C</h3>
                <div className="bg-orange-50 p-5 rounded-lg mb-6">
                  <h4 className="font-semibold mb-3 text-base">Pure L-Ascorbic Acid 15.5%</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• <strong>Water-based system</strong> - Superior absorption vs oil-based</li>
                    <li>• <strong>Stability breakthrough</strong> - No degradation or oxidation</li>
                    <li>• <strong>Non-irritating formula</strong> - Gentle yet effective</li>
                    
                  </ul>
                </div>
                <div className="w-full h-64 rounded-lg overflow-hidden">
                  <img src="./images/skin.png" alt="Vitamin C Stability Chart" className="w-full h-full object-contain bg-white" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Card 6: Why Partner with d.nuvo */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Users className="w-6 h-6 text-green-600" />
              <span>Why Partner with d.nuvo?</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-green-600">Brand Benefits</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Award className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Premium Brand Association</h4>
                      <p className="text-sm text-gray-600">Align with science-driven, luxury skincare brand</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Credible Science Backing</h4>
                      <p className="text-sm text-gray-600">37+ years expertise, patented technology</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Growing Market Presence</h4>
                      <p className="text-sm text-gray-600">Expanding across Southeast Asia markets</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3 text-purple-600">Content Opportunities</h3>
                <div className="space-y-3">
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-sm mb-1">Before & After Content</h4>
                    <p className="text-xs text-gray-600">Visible results with science-backed products</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-sm mb-1">Educational Content</h4>
                    <p className="text-xs text-gray-600">Share skincare science and ingredient benefits</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-sm mb-1">Routine Integration</h4>
                    <p className="text-xs text-gray-600">Easy integration into existing skincare routines</p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-sm mb-1">Unboxing & Reviews</h4>
                    <p className="text-xs text-gray-600">Premium packaging and product experience</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Card 7: Target Audience & Partnership */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle>Target Audience & Partnership</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="font-semibold mb-2">Primary Audience</h3>
                <p className="text-sm text-gray-600">Women 25-45, skincare enthusiasts, premium beauty consumers</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Interests</h3>
                <p className="text-sm text-gray-600">Science-backed skincare, anti-aging, ingredient education</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="font-semibold mb-2">Values</h3>
                <p className="text-sm text-gray-600">Quality, efficacy, transparency, scientific innovation</p>
              </div>
            </div>
            
            {/* Ready to Partner Section */}
            <div className="mt-8 p-6 dnuvo-gradient text-white rounded-lg print-header">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-4 print:text-black">Ready to Partner with d.nuvo?</h2>
              <p className="text-white/80 mb-6 print:text-black">Join us in revolutionizing skincare through science-driven innovation</p>
              
              {/* CTAs */}
              <div className="mb-6 text-center">
                <h3 className="font-bold mb-4 text-xl">Experience d.nuvo® Today</h3>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="https://shopee.sg/d.nuvo" target="_blank" rel="noopener noreferrer" className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2">
                    <span>🛍️ Shop on Shopee</span>
                  </a>
                  <a href="https://dnuvo.com.sg" target="_blank" rel="noopener noreferrer" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2">
                    <span>🌐 Visit Website</span>
                  </a>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 text-left">
                <div>
                  <h3 className="font-semibold mb-2">Partnership Contact : Marketing@Etheria-skincare.com</h3>
                  <p className="text-white/80 text-sm">Etheria Skincare Pte Ltd</p>
                  <p className="text-white/80 text-sm">Singapore • Malaysia • Indonesia • Philippines</p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Brand Assets</h3>
                  <a href="https://drive.google.com/drive/folders/1C4MjI4qwDb8uy0b9pzuDGk_7rhG6WmyG?usp=sharing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors text-sm mb-2">
                    <span>📁 Download Brand Assets</span>
                  </a>
                  <p className="text-white/80 text-sm print:text-black">High-resolution product images, brand guidelines, and marketing materials</p>
                </div>
              </div>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* FAQ Card */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-6 h-6 text-purple-600" />
              <span>Frequently Asked Questions</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="border-l-4 border-purple-400 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-purple-800">Q1. What makes d.nuvo different from other derma skincare brands?</h4>
                <p className="text-gray-700 text-sm">Most brands focus on surface-level actives without solving the fundamental challenge of absorption. d.nuvo is built on two proprietary pillars: our patented Ceramided™ technology, which enables up to 15x deeper delivery of actives, and a highly stabilized Water-Based Pure Vitamin C formula for unmatched efficacy. This offers a dual benefit—deeper absorption clinical results combined with a stronger skin barrier—all backed by the 37-year legacy of d.nuvo's master cosmetic scientist.</p>
              </div>
              
              <div className="border-l-4 border-purple-400 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-purple-800">Q2. Why is Ceramided™ a breakthrough compared to the liposomes used by competitors?</h4>
                <p className="text-gray-700 text-sm">Traditional liposomes are notoriously unstable and often degrade before delivering their payload. Our proprietary Ceramided™ capsules are engineered using ceramides, structurally mimicking the skin's natural "cement." This makes them exceptionally stable and highly skin-friendly. They do not just transport actives; they actively integrate into the skin barrier as they deliver ingredients, simultaneously repairing and strengthening skin integrity.</p>
              </div>
              
              <div className="border-l-4 border-purple-400 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-purple-800">Q3. Why 15.5% Vitamin C? Isn't more always better?</h4>
                <p className="text-gray-700 text-sm">Not necessarily. Clinical dermatology confirms that the 15–20% concentration range for pure L ascorbic acid (LAA) represents the optimal sweet spot for achieving maximum photoaging results with minimal potential for irritation. d.nuvo's water-based, ceramide-stabilized formula ensures this high 15.5% concentration delivers maximum uptake and proven safety.</p>
              </div>
              
              <div className="border-l-4 border-purple-400 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-purple-800">Q4. What clinical proof supports d.nuvo's efficacy?</h4>
                <div className="text-gray-700 text-sm space-y-3">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p><strong>🔬 Enhanced Absorption Research:</strong></p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>15× deeper Vitamin C delivery</strong> - Human Skin Clinical Trial Center (2024)</li>
                      <li><strong>14× greater absorption rate</strong> vs conventional vitamin C formulas</li>
                      <li><strong>Ceramided™ penetration study</strong> - Advanced Dermatology Research Institute</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p><strong>✨ Visible Results Studies:</strong></p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>Brightness improvement in 3 days</strong> - Semyung University (2023)</li>
                      <li><strong>Freckle reduction in 2-4 weeks</strong> - Clinical efficacy study</li>
                      <li><strong>Pigmentation improvement</strong> - 87% of participants showed visible results</li>
                    </ul>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p><strong>🌿 Safety & Tolerance:</strong></p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>Non-irritating formula</strong> - Advanced Science Lab (2025)</li>
                      <li><strong>Dermatologist tested</strong> - Safe for sensitive skin</li>
                      <li><strong>Patch test approved</strong> - 0% irritation rate in clinical trials</li>
                    </ul>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p><strong>🎯 Multi-Benefit Clinical Proof:</strong></p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>Acne reduction:</strong> 73% improvement in 4 weeks</li>
                      <li><strong>Sebum control:</strong> 65% reduction in oil production</li>
                      <li><strong>Wrinkle reduction:</strong> 42% improvement in fine lines</li>
                      <li><strong>Barrier function:</strong> 58% improvement in skin hydration</li>
                    </ul>
                    <p className="mt-2 text-xs text-gray-600">Source: Semyung University Clinical Study (2023)</p>
                  </div>
                </div>
              </div>
              
              <div className="border-l-4 border-purple-400 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-purple-800">Q5. Who is the target consumer for d.nuvo?</h4>
                <p className="text-gray-700 text-sm">d.nuvo is designed for the discerning Southeast Asian consumer who is tired of empty marketing claims and seeks tangible, science-backed results. d.nuvo caters to skincare enthusiasts who want clinical-grade efficacy without the luxury price tag, as well as users with sensitive skin who cannot tolerate harsh activities. d.nuvo's formulas are specifically adapted for Southeast Asia's humid climate, ensuring effective results while remaining lightweight and comfortable on the skin.</p>
              </div>
              
              <div className="border-l-4 border-purple-400 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-purple-800">Q6. How does d.nuvo position itself in the competitive Southeast Asian market?</h4>
                <p className="text-gray-700 text-sm">d.nuvo enters a highly receptive Southeast Asian market where heat and humidity necessitate climate-adapted skincare. We are positioned as "Premium Science," bridging the gap between derma and luxury with breathable, non-comedogenic formulas explicitly built for tropical climates. Our science-first approach and proven delivery technology resonates deeply with the region's discerning, results-driven consumers.</p>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>

        {/* Link to Brand Guide Appendix */}
        <div className="print-card">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <span>Brand Guide for Content Creation</span>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">Access the complete content creation toolkit</p>
          </CardHeader>
          <CardContent>
            <div className="text-center p-8">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-blue-900 mb-4">📖 Complete Brand Guide Appendix</h3>
                <p className="text-gray-700 mb-6">Access detailed messaging, story angles, hashtags, and content creation guidelines in a separate comprehensive guide.</p>
                <a href="#/appendix" className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                  <span>🔗 Access Brand Guide Appendix</span>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-6">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">© 2025 d.nuvo® by Etheria Skincare Pte Ltd. All rights reserved.</p>
          <p className="text-xs text-gray-500 mt-2">This influencer kit contains confidential brand information. Please use responsibly.</p>
        </div>
      </div>
    </div>;
};
export default Index;